import { IonButton, IonRow } from '@ionic/react';
import React, { useCallback, useState } from 'react';
import { DqText } from 'src/components/dq-text';

import { useIntl } from 'react-intl';
import { FirmwareMessages } from 'src/intl/FirmwareMessages';
import './FileUploadForm.scss';
import { GenericMessages } from '../../../intl/GenericMessages';

export interface FileUploadFormProps {
  /** Triggers the request to close the modal and save the state object. */
  onSave: (uploadedFile: File) => void;
  /** Function triggered when the file selection is cleared */
  onClear?: () => void;
  /** Indicates the types of files that can be accepted by the uploader */
  acceptableFileTypes?: string;
  /** Helper text to accompany the upload input. */
  helperText?: string;
  /** Indicates if file info should be saved to component state */
  useFileState?: boolean;
  /** Text that appears above the file upload input. Defaults to FirmwareMessages.selectAFile contents if not provided. */
  headingText?: string;
  /** Text that appears within the file upload component, before a file is selected. Defaults to FirmwareMessages.selectAFile contents if not provided. */
  inputText?: string;
  /** Indicates whether the file upload component is in an error state. */
  hasError?: boolean;
}

export const FileUploadForm: React.FC<FileUploadFormProps> = ({
  onSave,
  acceptableFileTypes,
  helperText,
  useFileState,
  onClear,
  headingText,
  inputText,
  hasError,
}) => {
  const { formatMessage } = useIntl();
  const fileField = React.useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | undefined>();

  const handleFileChange = useCallback(
    (ev: any) => {
      const uploadedFile = ev.currentTarget.files[0];

      if (useFileState) {
        setFile(uploadedFile);
      }

      onSave(uploadedFile);

      // Reset file selection once file data has been moved to form.
      ev.target.value = null;
    },
    [onSave, useFileState],
  );

  const uploadNewFile = () => {
    setFile(undefined);
    if (!!onClear) {
      onClear();
    }
    document?.getElementById('upload-file-input')?.click();
  };

  return (
    <>
      <IonRow>
        <DqText variant="body">{headingText ?? formatMessage(FirmwareMessages.selectAFile)}</DqText>
      </IonRow>
      <div>
        <IonButton
          onClick={() => document?.getElementById('upload-file-input')?.click()}
          className={`upload-file-input-button ${hasError ? 'error-file-input' : ''}`}
          fill="clear"
        >
          {file ? (
            <IonRow className="ion-justify-content-start ion-align-items-start" style={{ width: '100%' }}>
              <DqText color="secondary" className="ion-text-start" style={{ textTransform: 'none' }}>
                {file.name}
              </DqText>
            </IonRow>
          ) : (
            <IonRow className="ion-justify-content-center">
              <DqText color="secondary">{inputText ?? formatMessage(FirmwareMessages.selectAFile)}</DqText>
            </IonRow>
          )}

          <input
            onChange={handleFileChange}
            ref={fileField}
            type="file"
            accept={acceptableFileTypes}
            className="upload-file-input"
            id="upload-file-input"
          />
        </IonButton>
      </div>
      {file && (
        <IonRow>
          <IonButton
            fill="clear"
            color="dark"
            size="small"
            onClick={uploadNewFile}
            style={{ textTransform: 'none', textDecoration: 'underline' }}
          >
            {formatMessage(GenericMessages.removeAndChooseFileLabel)}
          </IonButton>
        </IonRow>
      )}
      {helperText && (
        <IonRow>
          <DqText variant="body-strong">{helperText}</DqText>
        </IonRow>
      )}
    </>
  );
};

export default FileUploadForm;
